import { action, atom, select } from '@reatom/framework'
import BigNumber from 'bignumber.js'
import { numericFormatter } from 'react-number-format'

import { AsyncStateN, pipe } from '@blade/std-lib'
import { CurrencyModel } from '@root/entities/currency'
import {
  walletsByAddressAtom,
  type WalletModel,
} from '@root/entities/wallet/atoms'
import { NumberUtil } from '@root/shared'
import { reatomNumericInput } from '@root/shared/lib/reatom'

import { reatomCurrencyPrice } from '../services'

import type { Atom } from '@reatom/framework'
import type { WalletAddress } from '@root/entities/wallet'

export const getAmountByPercentage = (args: {
  amount: BigNumber
  percentage: number
}) => {
  if (args.percentage === 1) {
    return args.amount
  }
  return args.amount.multipliedBy(args.percentage)
}

export const createCurrencyBalanceAtom = (
  currencyAtom: Atom<CurrencyModel | undefined>,
  walletAtom: Atom<WalletModel | undefined>,
) => {
  const theAtom = atom((ctx) => {
    const currency = ctx.spy(currencyAtom)
    const wallet = ctx.spy(walletAtom)
    if (!wallet || !currency) {
      return BigNumber(0)
    }
    const asset = ctx.spy(wallet.assetsByCurrency)[
      CurrencyModel.toNativeIfWrapped(currency).address
    ]
    if (!asset) {
      return BigNumber(0)
    }
    return ctx.spy(asset.balance)
    // eslint-disable-next-line @reatom/unit-naming-rule
  }, `${walletAtom.__reatom.name}_${currencyAtom.__reatom.name}_balanceAtom`)

  return theAtom
}

export const reatomCurrencyInput = (
  {
    currencyAtom,
    walletAtom,
    initialRawAmount,
    initialAmount,
  }: {
    currencyAtom: Atom<CurrencyModel | undefined>
    walletAtom: Atom<WalletModel | WalletAddress | undefined>
    initialRawAmount?: string
    initialAmount?: BigNumber.Value
  },
  name?: string,
) => {
  const amountAtom = reatomNumericInput(
    {
      initialRawAmount,
      initialAmount,
    },
    `${name}.amountAtom`,
  )

  const resolvedWalletAtom = atom((ctx) => {
    const wallet = ctx.spy(walletAtom)
    if (typeof wallet === 'string') {
      const wallets = ctx.spy(walletsByAddressAtom)
      return wallets.get(wallet)
    }
    return wallet
  }, `${name}.resolvedWalletAtom`)

  const amountRawAtom = amountAtom.raw
  const setAmountRawAction = amountAtom.setRaw

  const balanceAtom = createCurrencyBalanceAtom(
    currencyAtom,
    resolvedWalletAtom,
  )
  const currencyPriceModelAtom = atom((ctx) => {
    const _address = select(ctx, (ctx) => ctx.spy(currencyAtom)?.address)
    const currency = ctx.get(currencyAtom)

    // eslint-disable-next-line @reatom/unit-naming-rule
    return reatomCurrencyPrice({ currency })
  }, `${name}.currencyPriceModelAtom`)

  const setMaximumAmount = action((ctx) => {
    const balance = ctx.get(balanceAtom)
    amountRawAtom(ctx, balance.toString())
  }, `${name}.setMaximumAmount`)

  const setQuickAmount = action((ctx, percentage: number) => {
    if (percentage === 1) {
      setMaximumAmount(ctx)
      return
    }

    const resultAmount = getAmountByPercentage({
      amount: ctx.get(balanceAtom),
      percentage,
    })

    const decimals = ctx.get(currencyAtom)?.decimals ?? 4
    const amount = numericFormatter(resultAmount.toString(), {
      decimalScale: decimals,
    })

    amountRawAtom(ctx, amount)
  }, `${name}.setQuickAmount`)

  const currencyPriceUsdStateAtom = atom((ctx) => {
    const usdPriceState = ctx.spy(ctx.spy(currencyPriceModelAtom))
    return pipe(
      usdPriceState,
      AsyncStateN.map((price) => price.priceUsd),
    )
  }, `${name}.currencyPriceUsdStateAtom`)

  const amountUsdStateAtom = atom((ctx) => {
    const amount = ctx.spy(amountAtom)
    const usdPriceState = ctx.spy(currencyPriceUsdStateAtom)

    return pipe(
      usdPriceState,
      AsyncStateN.map((priceUsd) => priceUsd.multipliedBy(amount)),
    )
  }, `${name}.amountUsdStateAtom`)

  const amountUsdAtom = atom((ctx) => {
    const state = ctx.spy(amountUsdStateAtom)
    return state.value
  }, `${name}.amountUsdAtom`)

  const amountBalancePercentageAtom = atom((ctx) => {
    const balance = ctx.spy(balanceAtom)
    const amount = ctx.spy(amountAtom)

    return NumberUtil.safeBigNumber(amount.dividedBy(balance), 0).toNumber()
  }, `${name}.amountBalancePercentageAtom`)

  const isMaxAmountAtom = atom((ctx) => {
    const balance = ctx.spy(balanceAtom)
    const amount = ctx.spy(amountAtom)

    return amount.eq(balance)
  }, `${name}.isMaxAmountAtom`)

  return {
    amountRawAtom,
    amountAtom,
    amountUsdStateAtom,
    amountUsdAtom,
    currencyPriceUsdStateAtom,
    setAmountRawAction,
    setMaximumAmount,
    setQuickAmount,
    balanceAtom,
    amountBalancePercentageAtom,
    isMaxAmountAtom,
  }
}

export type CurrencyInputModel = ReturnType<typeof reatomCurrencyInput>
